import type { SimulatorField } from '@vedalib/editor/lib/elements'
import cn from 'classnames'
import { useState, useEffect } from 'react'

import { FieldSwitcher, Form, FormField, genDataField } from 'components/formKit'
import Button, { IconButton } from 'components/uiKit/Button'
import Tooltip from 'components/uiKit/Dropdown/Tooltip'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { t } from 'services/Translation'

import * as s from './ValidationSettings.module.scss'

type ValidationValues = {
  withoutValidation: boolean
  validationOnValue: boolean
}

const genField = genDataField<ValidationValues>()

const withoutValidationField = genField({
  as: FieldSwitcher,
  name: 'withoutValidation',
  label: t('elements.simulator.validation.withoutValidation'),
  layout: 'horizontal',
  defaultValue: false,
  size: KitSize.S,
})

const validationOnValueField = genField({
  as: FieldSwitcher,
  name: 'validationOnValue',
  label: t('elements.simulator.validation.validationOnValue'),
  layout: 'horizontal',
  defaultValue: false,
  size: KitSize.S,
})

interface IValueItemProps {
  value: string
  index: number
  onChange: (index: number, value: string) => void
  onDelete: (index: number) => void
}

const ValueItem = ({ value, index, onChange, onDelete }: IValueItemProps) => {
  const [localValue, setLocalValue] = useState(value)

  useEffect(() => {
    setLocalValue(value)
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setLocalValue(newValue)
    onChange(index, newValue)
  }

  return (
    <div className={s.valueItem}>
      <input
        className={s.valueInput}
        onChange={handleChange}
        placeholder={t('elements.simulator.validation.valuePlaceholder')}
        type='text'
        value={localValue}
      />
      <Tooltip overlay={t('elements.simulator.tooltip.delete')}>
        <IconButton
          icon='trashDeleteBin'
          iconSize={KitSize.XS}
          name='deleteValue'
          onClick={() => onDelete(index)}
          size={KitSize.XS}
          styleType='ghost'
        />
      </Tooltip>
    </div>
  )
}

interface IValidationSettingsProps {
  field: SimulatorField
  onUpdateField: (values: Partial<SimulatorField>) => void
}

const ValidationSettings = ({ field, onUpdateField }: IValidationSettingsProps) => {
  const onFinish = (values: ValidationValues) => {
    onUpdateField(values)
  }

  const handleAddValue = () => {
    const currentValues = field.validationValues || []
    onUpdateField({ validationValues: [...currentValues, ''] })
  }

  const handleChangeValue = (index: number, value: string) => {
    const currentValues = [...(field.validationValues || [])]
    currentValues[index] = value
    onUpdateField({ validationValues: currentValues })
  }

  const handleDeleteValue = (index: number) => {
    const currentValues = [...(field.validationValues || [])]
    currentValues.splice(index, 1)
    onUpdateField({ validationValues: currentValues })
  }

  const initialValues: ValidationValues = {
    withoutValidation: field.withoutValidation ?? false,
    validationOnValue: field.validationOnValue ?? false,
  }

  const validationValues = field.validationValues || []

  return (
    <div className={s.root}>
      <Form
        initialValues={initialValues}
        layout='vertical'
        name='validationSettings'
        onChangeValues={onFinish}
      >
        <div className={s.form}>
          <div className={s.field}>
            <FormField config={withoutValidationField} />
          </div>
          {!field.withoutValidation && (
            <>
              <div className={s.field}>
                <FormField config={validationOnValueField} />
              </div>
              {field.validationOnValue && (
                <>
                  <div className={cn(s.valueList, { [s.empty]: validationValues.length === 0 })}>
                    {validationValues.map((value, index) => (
                      <ValueItem
                        index={index}
                        key={index}
                        onChange={handleChangeValue}
                        onDelete={handleDeleteValue}
                        value={value}
                      />
                    ))}
                  </div>
                  <div className={s.button}>
                    <Button
                      name='addValue'
                      onClick={handleAddValue}
                      size={KitSize.S}
                      styleType='secondary'
                      fluid
                    >
                      <Icon name='otherAdd' size={KitSize.S} />
                      {t('elements.simulator.validation.addValue')}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Form>
    </div>
  )
}

export default ValidationSettings
